import { outputDiv } from "commonAssets/js/constants";
import { createElement } from "commonAssets/js/helpers";

/**
 * Handles the custom warranty claim process by rendering the login form
 * and executing the associated script for the given page.
 *
 * @param {Object} page - The page object representing the current page context.
 */
export function customWarrantyClaim(page) {
    const bodyHtml = loginFormHtml;
    const body = createElement("div", { class: "page-body" }, [], bodyHtml);
    outputDiv.appendChild(body);

    loginFormScript(page);
}

/**
 * HTML template for the login form used in the Extended Coverage and
 * Annual Sunroof Drain Cleaning Portal. This template includes a
 * password input field, a submit button, and placeholders for error
 * and success messages.
 *
 * @constant {string} loginFormHtml
 * @description A string containing the HTML structure for the login form.
 * - The form includes:
 *   - A password input field with validation.
 *   - A submit button for form submission.
 *   - Containers for displaying error and success messages.
 */
const loginFormHtml = `
    <div class="row fs-5">
        <div class="login-form col-md-6 offset-md-3 mb-5">
            <form class="needs-validation border rounded p-3" id="login-form" method="POST" novalidate>

                <div class="h4 text-center mb-3">
                    Extended Coverage &<br>Annual Sunroof Drain Cleaning Portal
                </div>
                <div class="form-floating mb-3">
                    <input
                        type="password"
                        class="form-control"
                        id="password"
                        name="password"
                        placeholder="Password"
                        autocomplete="off"
                        required>
                    <label for="password">Password</label>
                </div>
                <button class="btn btn-primary btn-block" type="submit">Sign In</button>
            </form>
            <div class="error-message" id="login-error"></div>
            <div class="success-message" id="login-success"></div>
        </div>
    </div>`;

/**
 * Attaches a submit event listener to the login form, validates the password input,
 * and performs actions based on the validation result.
 *
 * @param {string} page - The name of the page to be used for local storage and redirection.
 *
 * The function performs the following steps:
 * 1. Prevents the default form submission behavior.
 * 2. Validates the password field to ensure it is not empty.
 * 3. Checks if the password matches a predefined encoded value.
 * 4. Displays appropriate error messages for invalid or missing passwords.
 * 5. Clears the password field and displays a success message upon successful validation.
 * 6. Calls `setLocalStorageKeyValue` and `forwardToSubmitClaim` with the provided page parameter.
 */
const loginFormScript = (page) => {
    const form = document.getElementById("login-form");
    form.addEventListener("submit", (event) => {
        event.preventDefault();

        const password = document.getElementById("password");
        const value = password.value;

        if (value === "") {
            document.getElementById("login-error").innerHTML =
                `Error: Password is required`;
            return;
        }

        if (
            btoa(
                decodeURIComponent(encodeURIComponent(value.toUpperCase()))
            ) !== "UENOQTIwMjUh"
        ) {
            document.getElementById("password").value = "";
            document.getElementById("login-error").innerHTML =
                `Error: Invalid Password`;
            return;
        }

        document.getElementById("password").value = "";
        document.getElementById("login-error").innerHTML = "";
        document.getElementById("login-success").innerHTML = `Please wait...`;

        setLocalStorageKeyValue(page);
        forwardToSubmitClaim(page);
    });
};

/**
 * Sets specific key-value pairs in the localStorage based on the provided page identifier.
 *
 * @param {string} page - The identifier for the page.
 *                        Acceptable values are:
 *                        - 'submit-claim': Sets keys for the submit claim page.
 *                        - 'vin-check': Sets keys for the VIN check page.
 *
 * The function sets the following key-value pairs in localStorage:
 * - For 'submit-claim':
 *   - Key: 'submitClaim', Value: 'true'
 *   - Key: 'submitClaimForm', Value: 'true'
 * - For 'vin-check':
 *   - Key: 'warrantyClaim', Value: 'true'
 *   - Key: 'warrantyClaimForm', Value: 'true'
 */

const pageMappings = {
    "submit-claim": {
        pageName: "submitClaim",
        formName: "submitClaimForm",
    },
    "vin-check": {
        pageName: "warrantyClaim",
        formName: "warrantyClaimForm",
    },
};

const setLocalStorageKeyValue = (page) => {
    const mapping = pageMappings[page];

    if (mapping) {
        localStorage.setItem(mapping.pageName, "true");
        localStorage.setItem(mapping.formName, "true");
    } else {
        console.error(`No mapping found for page: ${page}`);
    }
};

/**
 * Redirects the user to a specific page based on the provided page identifier.
 *
 * @param {string} page - The identifier of the page to redirect to.
 *                         Accepted values are:
 *                         - 'submit-claim': Redirects to the submit claim form.
 *                         - 'vin-check': Redirects to the VIN check form.
 */
const forwardToSubmitClaim = (page) => {
    if (page === "submit-claim") {
        window.location.href = "/submit-claim-form";
        return;
    } else if (page === "vin-check") {
        window.location.href = "/vin-check-form";
        return;
    }
};
